import React from "react";
import { InfoIcon, Trash } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Textarea } from "../ui/textarea";

// Pure utility function
export const getTextColor = (backgroundColor) => {
  if (!backgroundColor || backgroundColor.toLowerCase() === "white" || backgroundColor === "#fff" || backgroundColor === "#ffffff") {
    return "black";
  }
  const hex = backgroundColor.replace("#", "");
  if (hex.length !== 6) return "black";
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "black" : "white";
};

// Common color options for both table types
export const colorOptions = [
  { name: "Red", value: "#FF0000" },
  { name: "Orange", value: "#FFA500" },
  { name: "Yellow", value: "#FFFF00" },
  { name: "Green", value: "#008000" },
  { name: "Light Green", value: "#90EE90" },
  { name: "Pink", value: "#FFC0CB" },
  { name: "Burgundy", value: "#800020" },
  { name: "Blue", value: "#0000FF" },
  { name: "Light Blue", value: "#ADD8E6" },
  { name: "Purple", value: "#800080" },
  { name: "Gray", value: "#808080" },
];

// Common diagonal divider header component
const DiagonalDividerHeader = ({ topText, bottomText }) => (
  <th className="border p-0 text-xs text-center relative" style={{ width: "200px", minWidth: "200px" }}>
    <div className="w-full h-full">
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="w-full h-full relative">
          <div className="absolute top-0 right-0 w-1/2 h-1/2 flex items-end justify-end text-xs p-1">
            {topText}
          </div>
          <div className="absolute bottom-0 left-0 w-1/2 h-1/2 flex items-start justify-start text-xs p-1">
            {bottomText}
          </div>
          <div
            className="absolute top-0 left-0 w-full h-full"
            style={{
              background:
                "linear-gradient(to top right, transparent calc(50% - 1px), black, transparent calc(50% + 1px))",
              pointerEvents: "none",
            }}
          ></div>
        </div>
      </div>
    </div>
  </th>
);

// Component for normal matrix display (probability as rows, severity as columns)
const NormalMatrixTable = ({ 
  matrixData, 
  descriptions, 
  diagonalText, 
  dispatch, 
  editingCell, 
  setEditingCell, 
  editingCellValue,
  setEditingCellValue,
  currentCellColor,
  setCurrentCellColor,
  updateDescription
}) => {
  const inputRefs = React.useRef({});
  const descRefs = React.useRef({});

  // Use refs to track focus state instead of state variables to avoid the unused variable warning
  const focusTypeRef = React.useRef(null);
  const focusIndexRef = React.useRef(null);

  React.useEffect(() => {
    if (focusTypeRef.current && focusIndexRef.current !== null) {
      if (focusTypeRef.current === 'sev') {
        inputRefs.current[`sev-${focusIndexRef.current}`]?.focus();
      } else if (focusTypeRef.current === 'sev-desc') {
        descRefs.current[`sev-${focusIndexRef.current}`]?.focus();
      } else if (focusTypeRef.current === 'prob') {
        inputRefs.current[`prob-${focusIndexRef.current}`]?.focus();
      } else if (focusTypeRef.current === 'prob-desc') {
        descRefs.current[`prob-${focusIndexRef.current}`]?.focus();
      }
    }
  }, []);

  return (
    <table className="border-collapse w-full text-xs">
      <thead>
        <tr>
          <DiagonalDividerHeader topText={diagonalText.top} bottomText={diagonalText.bottom} />
          {matrixData.severities.map((item, index) => (
            <th key={index} className="border px-2 py-1 min-w-[60px]">
              <div className="w-full flex items-center gap-1">
                <Input
                  ref={el => inputRefs.current[`sev-${index}`] = el}
                  value={item}
                  onChange={e => dispatch({ type: 'UPDATE_SEVERITY', index, value: e.target.value })}
                  className="w-full text-center font-bold text-xs px-2 py-1 h-7"
                  size="sm"
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 ml-1"
                  onClick={() => dispatch({ type: 'REMOVE_SEVERITY', index })}
                  tabIndex={-1}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex justify-center mt-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <span>
                      <Button variant="ghost" size="icon" className="h-5 w-5 p-0 mx-auto block">
                        <InfoIcon className="h-3 w-3" />
                      </Button>
                    </span>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2">
                    <div className="space-y-1">
                      <h4 className="font-medium text-xs">
                        Description for {item || `Severity ${index + 1}`}
                      </h4>
                      <Textarea
                        ref={el => descRefs.current[`sev-${index}`] = el}
                        value={descriptions.severities[index] || ""}
                        onChange={e => updateDescription("severities", index, e.target.value)}
                        placeholder={`Describe ${item || `this severity level`}`}
                        className="min-h-[60px] text-xs"
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {matrixData.matrix.map((row, rowIndex) => (
          <tr key={rowIndex}>
            <td className="border px-2 py-1 align-top min-w-[60px]">
              <div className="w-full flex items-center gap-1">
                <Input
                  ref={el => inputRefs.current[`prob-${rowIndex}`] = el}
                  value={matrixData.probabilities[rowIndex]}
                  onChange={e => dispatch({ type: 'UPDATE_PROBABILITY', index: rowIndex, value: e.target.value })}
                  className="w-full text-center font-bold text-xs px-2 py-1 h-7"
                  size="sm"
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 ml-1"
                  onClick={() => dispatch({ type: 'REMOVE_PROBABILITY', index: rowIndex })}
                  tabIndex={-1}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex justify-center mt-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <span>
                      <Button variant="ghost" size="icon" className="h-5 w-5 p-0 mx-auto block">
                        <InfoIcon className="h-3 w-3" />
                      </Button>
                    </span>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2">
                    <div className="space-y-1">
                      <h4 className="font-medium text-xs">
                        Description for {matrixData.probabilities[rowIndex] || `Probability ${rowIndex + 1}`}
                      </h4>
                      <Textarea
                        ref={el => descRefs.current[`prob-${rowIndex}`] = el}
                        value={descriptions.probabilities[rowIndex] || ""}
                        onChange={e => updateDescription("probabilities", rowIndex, e.target.value)}
                        placeholder={`Describe ${matrixData.probabilities[rowIndex] || `this probability level`}`}
                        className="min-h-[60px] text-xs"
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </td>
            {row.map((cell, colIndex) => (
              <td key={colIndex} className="border px-2 py-1 text-center align-middle min-w-[40px] h-[32px]">
                <div className="w-full h-full flex items-center gap-1">
                  <div
                    className="w-full h-[32px] flex items-center justify-center cursor-pointer"
                    style={{
                      backgroundColor: matrixData.colors[cell] || "white",
                      color: matrixData.colors[cell] ? getTextColor(matrixData.colors[cell]) : "black",
                    }}
                    tabIndex={0}
                    onClick={() => {
                      setEditingCell(`${rowIndex}-${colIndex}`)
                      setEditingCellValue(cell)
                      setCurrentCellColor(matrixData.colors[cell] || "white")
                    }}
                  >
                    {editingCell === `${rowIndex}-${colIndex}` ? (
                      <Input
                        value={editingCellValue}
                        onChange={e => setEditingCellValue(e.target.value)}
                        className="w-full h-full text-center border-none bg-transparent text-xs px-2 py-1"
                        style={{ color: getTextColor(currentCellColor) }}
                        autoFocus
                        onBlur={() => {
                          const prevValue = matrixData.matrix[rowIndex][colIndex];
                          dispatch({
                            type: 'UPDATE_CELL',
                            rowIndex: rowIndex,
                            colIndex: colIndex,
                            value: editingCellValue,
                            prevValue,
                            color: currentCellColor
                          });
                          setEditingCell(null)
                        }}
                        onKeyDown={e => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const prevValue = matrixData.matrix[rowIndex][colIndex];
                            dispatch({
                              type: 'UPDATE_CELL',
                              rowIndex: rowIndex,
                              colIndex: colIndex,
                              value: editingCellValue,
                              prevValue,
                              color: currentCellColor
                            });
                            setEditingCell(null);
                          }
                          else if (e.key === "Tab" && !e.shiftKey) {
                            e.preventDefault();
                            
                            // Save current cell first
                            const prevValue = matrixData.matrix[rowIndex][colIndex];
                            dispatch({
                              type: 'UPDATE_CELL',
                              rowIndex: rowIndex,
                              colIndex: colIndex,
                              value: editingCellValue,
                              prevValue,
                              color: currentCellColor
                            });
                            
                            // Navigate to next cell
                            const nextCol = colIndex + 1;
                            const nextRow = rowIndex + (nextCol >= row.length ? 1 : 0);
                            const nextCell = nextCol < row.length
                              ? `${rowIndex}-${nextCol}`
                              : nextRow < matrixData.matrix.length
                                ? `${nextRow}-0`
                                : null;
                            if (nextCell) {
                              setEditingCell(nextCell);
                              setEditingCellValue(
                                nextCol < row.length
                                  ? matrixData.matrix[rowIndex][nextCol]
                                  : matrixData.matrix[nextRow][0]
                              );
                              setCurrentCellColor(
                                nextCol < row.length
                                  ? matrixData.colors[matrixData.matrix[rowIndex][nextCol]] || "white"
                                  : matrixData.colors[matrixData.matrix[nextRow][0]] || "white"
                              );
                            } else {
                              e.target.blur();
                            }
                          }
                        }}
                      />
                    ) : (
                      cell
                    )}
                  </div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-6 w-6 p-0 rounded-full border border-gray-300 ml-1"
                        style={{
                          backgroundColor: matrixData.colors[cell] || "white",
                        }}
                        tabIndex={-1}
                      />
                    </PopoverTrigger>
                    <PopoverContent className="w-40 p-2">
                      <div className="space-y-1">
                        <h4 className="font-medium text-xs">Select Color</h4>
                        <div className="flex flex-wrap gap-1">
                          {colorOptions.map((color) => (
                            <button
                              key={color.value}
                              className={`w-6 h-6 rounded-full border flex items-center justify-center p-0 ${
                                (matrixData.colors[cell] || "white") === color.value
                                  ? "border-blue-500 ring-2 ring-blue-300"
                                  : "border-gray-300"
                              }`}
                              style={{ backgroundColor: color.value }}
                              onClick={() => {
                                if (cell) {
                                  dispatch({
                                    type: 'UPDATE_COLOR',
                                    rating: cell,
                                    color: color.value
                                  });
                                }
                              }}
                              title={color.name}
                            />
                          ))}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// Component for flipped matrix display (severity as rows, probability as columns)
const FlippedMatrixTable = ({ 
  matrixData, 
  descriptions, 
  diagonalText, 
  dispatch, 
  editingCell, 
  setEditingCell, 
  editingCellValue,
  setEditingCellValue,
  currentCellColor,
  setCurrentCellColor,
  updateDescription
}) => {
  const inputRefs = React.useRef({});
  const descRefs = React.useRef({});

  const transposedMatrix = matrixData.matrix[0].map((_, colIndex) => 
    matrixData.matrix.map(row => row[colIndex])
  );

  return (
    <table className="border-collapse w-full text-xs">
      <thead>
        <tr>
          <DiagonalDividerHeader topText={diagonalText.top} bottomText={diagonalText.bottom} />
          {matrixData.probabilities.map((item, index) => (
            <th key={index} className="border px-2 py-1 min-w-[60px]">
              <div className="w-full flex items-center gap-1">
                <Input
                  ref={el => inputRefs.current[`prob-${index}`] = el}
                  value={item}
                  onChange={e => dispatch({ type: 'UPDATE_PROBABILITY', index, value: e.target.value })}
                  className="w-full text-center font-bold text-xs px-2 py-1 h-7"
                  size="sm"
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 ml-1"
                  onClick={() => dispatch({ type: 'REMOVE_PROBABILITY', index })}
                  tabIndex={-1}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex justify-center mt-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <span>
                      <Button variant="ghost" size="icon" className="h-5 w-5 p-0 mx-auto block">
                        <InfoIcon className="h-3 w-3" />
                      </Button>
                    </span>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2">
                    <div className="space-y-1">
                      <h4 className="font-medium text-xs">
                        Description for {item || `Probability ${index + 1}`}
                      </h4>
                      <Textarea
                        ref={el => descRefs.current[`prob-${index}`] = el}
                        value={descriptions.probabilities[index] || ""}
                        onChange={e => updateDescription("probabilities", index, e.target.value)}
                        placeholder={`Describe ${item || `this probability level`}`}
                        className="min-h-[60px] text-xs"
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {transposedMatrix.map((row, rowIndex) => (
          <tr key={rowIndex}>
            <td className="border px-2 py-1 align-top min-w-[60px]">
              <div className="w-full flex items-center gap-1">
                <Input
                  ref={el => inputRefs.current[`sev-${rowIndex}`] = el}
                  value={matrixData.severities[rowIndex]}
                  onChange={e => dispatch({ type: 'UPDATE_SEVERITY', index: rowIndex, value: e.target.value })}
                  className="w-full text-center font-bold text-xs px-2 py-1 h-7"
                  size="sm"
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 ml-1"
                  onClick={() => dispatch({ type: 'REMOVE_SEVERITY', index: rowIndex })}
                  tabIndex={-1}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex justify-center mt-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <span>
                      <Button variant="ghost" size="icon" className="h-5 w-5 p-0 mx-auto block">
                        <InfoIcon className="h-3 w-3" />
                      </Button>
                    </span>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2">
                    <div className="space-y-1">
                      <h4 className="font-medium text-xs">
                        Description for {matrixData.severities[rowIndex] || `Severity ${rowIndex + 1}`}
                      </h4>
                      <Textarea
                        ref={el => descRefs.current[`sev-${rowIndex}`] = el}
                        value={descriptions.severities[rowIndex] || ""}
                        onChange={e => updateDescription("severities", rowIndex, e.target.value)}
                        placeholder={`Describe ${matrixData.severities[rowIndex] || `this severity level`}`}
                        className="min-h-[60px] text-xs"
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </td>
            {row.map((cell, colIndex) => {
              const actualCell = matrixData.matrix[colIndex][rowIndex];
              return (
                <td key={colIndex} className="border px-2 py-1 text-center align-middle min-w-[40px] h-[32px]">
                  <div className="w-full h-full flex items-center gap-1">
                    <div
                      className="w-full h-[32px] flex items-center justify-center cursor-pointer"
                      style={{
                        backgroundColor: matrixData.colors[actualCell] || "white",
                        color: matrixData.colors[actualCell] ? getTextColor(matrixData.colors[actualCell]) : "black",
                      }}
                      tabIndex={0}
                      onClick={() => {
                        setEditingCell(`flipped-${rowIndex}-${colIndex}`)
                        setEditingCellValue(actualCell)
                        setCurrentCellColor(matrixData.colors[actualCell] || "white")
                      }}
                    >
                      {editingCell === `flipped-${rowIndex}-${colIndex}` ? (
                        <Input
                          value={editingCellValue}
                          onChange={e => setEditingCellValue(e.target.value)}
                          className="w-full h-full text-center border-none bg-transparent text-xs px-2 py-1"
                          style={{ color: getTextColor(currentCellColor) }}
                          autoFocus
                          onBlur={() => {
                            const prevValue = matrixData.matrix[colIndex][rowIndex];
                            dispatch({
                              type: 'UPDATE_CELL',
                              rowIndex: colIndex,
                              colIndex: rowIndex,
                              value: editingCellValue,
                              prevValue,
                              color: currentCellColor
                            });
                            setEditingCell(null)
                          }}
                          onKeyDown={e => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              const prevValue = matrixData.matrix[colIndex][rowIndex];
                              dispatch({
                                type: 'UPDATE_CELL',
                                rowIndex: colIndex,
                                colIndex: rowIndex,
                                value: editingCellValue,
                                prevValue,
                                color: currentCellColor
                              });
                              setEditingCell(null);
                            }
                            else if (e.key === "Tab" && !e.shiftKey) {
                              e.preventDefault();
                              
                              // Save current cell first
                              const prevValue = matrixData.matrix[colIndex][rowIndex];
                              dispatch({
                                type: 'UPDATE_CELL',
                                rowIndex: colIndex,
                                colIndex: rowIndex,
                                value: editingCellValue,
                                prevValue,
                                color: currentCellColor
                              });
                              
                              // Navigate to next cell
                              const nextCol = colIndex + 1;
                              const nextRow = rowIndex + (nextCol >= row.length ? 1 : 0);
                              const nextCell = nextCol < row.length
                                ? `flipped-${rowIndex}-${nextCol}`
                                : nextRow < transposedMatrix.length
                                  ? `flipped-${nextRow}-0`
                                  : null;
                              if (nextCell) {
                                setEditingCell(nextCell);
                                setEditingCellValue(
                                  nextCol < row.length
                                    ? transposedMatrix[rowIndex][nextCol]
                                    : transposedMatrix[nextRow][0]
                                );
                                setCurrentCellColor(
                                  nextCol < row.length
                                    ? matrixData.colors[transposedMatrix[rowIndex][nextCol]] || "white"
                                    : matrixData.colors[transposedMatrix[nextRow][0]] || "white"
                                );
                              } else {
                                e.target.blur();
                              }
                            }
                          }}
                        />
                      ) : (
                        actualCell
                      )}
                    </div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="h-6 w-6 p-0 rounded-full border border-gray-300 ml-1"
                          style={{
                            backgroundColor: matrixData.colors[actualCell] || "white",
                          }}
                          tabIndex={-1}
                        />
                      </PopoverTrigger>
                      <PopoverContent className="w-40 p-2">
                        <div className="space-y-1">
                          <h4 className="font-medium text-xs">Select Color</h4>
                          <div className="flex flex-wrap gap-1">
                            {colorOptions.map((color) => (
                              <button
                                key={color.value}
                                className={`w-6 h-6 rounded-full border flex items-center justify-center p-0 ${
                                  (matrixData.colors[actualCell] || "white") === color.value
                                    ? "border-blue-500 ring-2 ring-blue-300"
                                    : "border-gray-300"
                                }`}
                                style={{ backgroundColor: color.value }}
                                onClick={() => {
                                  if (actualCell) {
                                    dispatch({
                                      type: 'UPDATE_COLOR',
                                      rating: actualCell,
                                      color: color.value
                                    });
                                  }
                                }}
                                title={color.name}
                              />
                            ))}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export { NormalMatrixTable, FlippedMatrixTable };
